import { useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import {
  tariffBookKeys,
  tariffBookStaleTime,
} from 'features/buku-tarif/shared/api/queryKeys';
import { getTariffBookList } from 'features/buku-tarif/shared/api/getTariffBookList';
import { getErrorMessage } from 'shared/utils/errorMessage';
import { showErrorToastWithRetry } from 'shared/utils/showErrorToastWithRetry';
import { useDebounce } from 'shared/hooks/useDebounce';
import {
  DEFAULT_SORT,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
} from '../components/tariff-book-table/tariff-book-table.constant';

export const useTariffBookList = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || DEFAULT_SORT;
  const page = parseInt(searchParams.get('page') || String(DEFAULT_PAGE), 10);
  const pageSize = parseInt(
    searchParams.get('pageSize') || String(DEFAULT_PAGE_SIZE),
    10
  );

  const dateStart = searchParams.get('dateStart')
    ? dayjs(searchParams.get('dateStart'), 'YYYY-MM-DD').toDate()
    : null;
  const dateEnd = searchParams.get('dateEnd')
    ? dayjs(searchParams.get('dateEnd'), 'YYYY-MM-DD').toDate()
    : null;

  const debouncedSearch = useDebounce(search, 500);

  const queryParams = {
    search: debouncedSearch,
    page,
    sort,
    limit: pageSize,
    start_date: dateStart ? dayjs(dateStart).format('YYYY-MM-DD') : '',
    end_date: dateEnd ? dayjs(dateEnd).format('YYYY-MM-DD') : '',
  };

  const { data, isFetching, refetch } = useQuery(
    tariffBookKeys.list(queryParams),
    ({ signal }) => getTariffBookList(queryParams, signal),
    {
      staleTime: tariffBookStaleTime.LIST,
      keepPreviousData: true,
      retry: (failureCount, error) => {
        const status = error?.status || error?.response?.status || 500;
        return status >= 500 && failureCount < 3;
      },
      retryDelay: (retryAttempt) => retryAttempt * 3000,
      onError: (error) => {
        const status = error?.status || error?.response?.status || 500;
        const message = getErrorMessage(error);

        if (status >= 500) {
          showErrorToastWithRetry(`List Buku Tariff: ${message}`, () =>
            refetch()
          );
        } else {
          toast.error(`List Buku Tariff: ${message}`);
        }
      },
    }
  );

  const updateSearchParams = (params) => {
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      return newParams;
    });
  };

  const handleSearchChange = (e) => {
    updateSearchParams({ page: 1, search: e.target.value });
  };

  const handleSortOrderChange = (newSort) => {
    updateSearchParams({ sort: newSort });
  };

  const handlePaginationChange = (newPage, newPageSize) => {
    updateSearchParams({ page: newPage, pageSize: newPageSize });
  };

  const handlePageSizeChange = (newPageSize) => {
    updateSearchParams({ page: 1, pageSize: newPageSize });
  };

  const handleDateRangeChange = (range) => {
    if (!range || range.length < 2) {
      return;
    }
    updateSearchParams({
      page: 1,
      dateStart: range[0] ? dayjs(range[0]).format('YYYY-MM-DD') : '',
      dateEnd: range[1] ? dayjs(range[1]).format('YYYY-MM-DD') : '',
    });
  };

  return {
    data,
    isFetching,
    refetch,
    search,
    sort,
    page,
    pageSize,
    dateStart,
    dateEnd,
    handleSearchChange,
    handleSortOrderChange,
    handlePaginationChange,
    handlePageSizeChange,
    handleDateRangeChange,
  };
};
